import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  fetchPersonProfileById,
  updateMyProfile,
  type Gender,
  type PersonProfileFields,
} from "@/api/profile";
import { hasNonLatin } from "@/lib/latinName";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  ProfileFields,
  type ProfileFieldsValue,
  type ProfileFieldsErrors,
} from "@/components/profile/ProfileFields";

/** Gender or home location missing → we never captured them (plain Google
 *  sign-in skips the registration form that collects them). */
function needsCompletion(p: PersonProfileFields): boolean {
  return !p.gender || !p.home_city;
}

/**
 * Post-sign-in nudge: plain Google sign-in never collects gender/location, so
 * those stay null and city-level stats miss the person. When a signed-in
 * person is missing either, prompt once. Dismissible (not a hard block) and
 * skipped entirely for people who already have both — event registration
 * already captures these, so registered users won't see it.
 */
export function ProfileCompletionDialog() {
  const { personId } = useAuth();
  const { toast } = useToast();
  const [person, setPerson] = useState<PersonProfileFields | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [value, setValue] = useState<ProfileFieldsValue>({
    firstName: "",
    lastName: "",
    gender: "unspecified",
    homeCity: "",
    homeCountry: null,
    phone: "",
  });

  useEffect(() => {
    if (personId == null) {
      setOpen(false);
      return;
    }
    let cancelled = false;
    fetchPersonProfileById(personId)
      .then((p) => {
        if (cancelled || !needsCompletion(p)) return;
        setPerson(p);
        setValue({
          firstName: p.first_name ?? "",
          lastName: p.last_name ?? "",
          gender: p.gender ?? "unspecified",
          homeCity: p.home_city ?? "",
          homeCountry: p.home_country,
          phone: p.phone ?? "",
        });
        setOpen(true);
      })
      .catch(() => {
        // best-effort nudge — never block sign-in on a fetch failure
      });
    return () => {
      cancelled = true;
    };
  }, [personId]);

  const patch = (p: Partial<ProfileFieldsValue>) =>
    setValue((v) => ({ ...v, ...p }));

  const firstOk = value.firstName.trim().length > 0 && !hasNonLatin(value.firstName);
  const lastOk = /\p{L}/u.test(value.lastName.trim()) && !hasNonLatin(value.lastName);
  const genderOk = value.gender !== "unspecified";
  const cityOk = value.homeCity.trim().length > 0;
  const valid = firstOk && lastOk && genderOk && cityOk;

  const errors: ProfileFieldsErrors = showErrors
    ? { firstName: !firstOk, lastName: !lastOk, gender: !genderOk }
    : {};

  const handleSave = async () => {
    if (!person) return;
    if (!valid) {
      setShowErrors(true);
      return;
    }
    setBusy(true);
    try {
      await updateMyProfile(person.id, {
        firstName: value.firstName.trim(),
        lastName: value.lastName.trim(),
        gender: value.gender as Gender,
        homeCity: value.homeCity.trim(),
        homeCountry: value.homeCountry,
        phone: value.phone.trim() || null,
      });
      setOpen(false);
      toast({ title: "Thanks — your profile's up to date." });
    } catch {
      toast({
        title: "Couldn't save your details",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Complete your profile</DialogTitle>
          <DialogDescription>
            Add your gender and location so we can show the right events and
            stats for your city. Takes a few seconds.
          </DialogDescription>
        </DialogHeader>
        <ProfileFields value={value} onChange={patch} errors={errors} />
        {showErrors && !cityOk ? (
          <p className="text-xs text-destructive">Please add your city.</p>
        ) : null}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={busy}
          >
            Later
          </Button>
          <Button onClick={handleSave} disabled={busy} aria-busy={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
